/**
 * After a deploy, the HTML shell can reference new hashed chunks while an edge
 * still serves an older asset set (or the opposite). Dynamic `import()` then
 * fails with "Failed to fetch dynamically imported module". One full reload
 * usually picks up a consistent HTML + assets pair.
 *
 * Session values: unset → (on failure) `pending` → after reload `done` so we
 * never loop if the chunk is genuinely missing.
 */
const SESSION_KEY = 'memara_chunk_recover_v1';

function messageFromUnknown(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

export function isLikelyStaleChunkLoadFailure(err: unknown): boolean {
  if (import.meta.env.DEV) return false;
  const msg = messageFromUnknown(err).toLowerCase();
  const name = err instanceof Error ? err.name : '';
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    name === 'ChunkLoadError'
  );
}

/**
 * Run synchronously at startup (before React). Converts `pending` → `done` on
 * the load after a recovery reload; clears the key on fresh navigations.
 */
export function initStaleChunkRecoverySession(): void {
  if (import.meta.env.DEV) return;
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const type = nav?.type;
    const key = sessionStorage.getItem(SESSION_KEY);

    if (type !== 'reload') {
      sessionStorage.removeItem(SESSION_KEY);
    } else if (key === 'pending') {
      sessionStorage.setItem(SESSION_KEY, 'done');
    } else {
      // Any other full reload (including manual refresh after a failed recovery)
      sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * If this looks like a stale chunk (deploy rotated hashed assets while the tab
 * still has an old shell), reload once per navigation session.
 */
export function maybeReloadOnceForStaleChunk(err: unknown): void {
  if (!isLikelyStaleChunkLoadFailure(err)) return;
  const state = sessionStorage.getItem(SESSION_KEY);
  if (state === 'done' || state === 'pending') return;
  sessionStorage.setItem(SESSION_KEY, 'pending');
  window.location.reload();
}
